import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subject,
  map,
  switchMap,
  takeUntil,
  catchError,
} from 'rxjs';

import {
  IPatientHistory,
  IPatientRequest,
  IPatientRequestDetail,
  IPatientRequestMaterial,
  LAB_TYPE,
  PatientRequestsService,
  SearchType,
} from '@app/core/patient-requests';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbItem } from '@app/shared/breadcrumb/breadcrumb.component';
import { LocationService } from '@app/core/location';
import { UsersService } from '@app/core/users/users.service';
import { MsgboxService } from '@app/shared/msgbox';
import { DialogService } from 'primeng/dynamicdialog';
import { AcumuladosSearchDialogComponent } from '../acumulados-search-dialog/acumulados-search-dialog.component';
import {
  EpidemiologySearchDialogComponent,
  type EpidemiologySearchDialogData,
} from '../epidemiology-search-dialog/epidemiology-search-dialog.component';

const BREADCRUMBS_WITHOUT_PATIENT: BreadcrumbItem[] = [
  {
    label: 'WEB_RESULTS.NAVIGATION.SEARCH_BY_REQUEST',
    route: '/patient-requests/',
    params: {
      searchType: 'search-by-request',
    },
  },
  {
    label: 'WEB_RESULTS.NAVIGATION.SEARCH_BY_REQUEST_RESULTS',
  },
];

const BREADCRUMBS_WITH_PATIENT: BreadcrumbItem[] = [
  {
    label: 'WEB_RESULTS.NAVIGATION.SEARCH_BY_PATIENT',
    route: '/patient-requests/',
    params: {
      searchType: 'search-by-patient',
    },
  },
  {
    label: 'WEB_RESULTS.NAVIGATION.SEARCH_BY_PATIENT_RESULTS',
    route: 'back(-1)',
  },
  {
    label: 'WEB_RESULTS.NAVIGATION.PATIENT_DETAIL',
  },
];

@Component({
  selector: 'afoone-patient-request-list',
  templateUrl: './patient-request-list.component.html',
  styleUrls: ['./patient-request-list.component.css'],
})
export class PatientRequestListComponent implements OnInit, OnDestroy {
  @ViewChild('selectedRequestResults')
  private selectedRequestResults?: ElementRef<HTMLElement>;

  public records$: Observable<IPatientRequest[]>;
  public records: IPatientRequest[] = [];

  public selectedRecord$: Subject<IPatientRequestDetail> = new Subject();
  public patient$: BehaviorSubject<IPatientHistory> = new BehaviorSubject(null);

  public selectedRequest: IPatientRequest;
  public selectedMaterial: IPatientRequestMaterial;

  public materiales: IPatientRequestMaterial[] = [];
  public currentHeader: IPatientRequestDetail;

  private dataFromState: IPatientRequest[] | null = null;

  private destroyed$ = new Subject<void>();

  public isPatientSearch: boolean = false;

  private _patientReceivedButNotComingFromHistory: boolean = false;

  public columns = [];

  public breadcrumbs: BreadcrumbItem[] = BREADCRUMBS_WITHOUT_PATIENT;

  public sortField: string = 'patVorname';
  public sortOrder: number = 1;

  /** True if any request has LabType LABC (acumulados available). For botón Acumulados. */
  get hasAnyLabTypeLABC(): boolean {
    return this.records.some((r) => (r.labType || '').trim().toUpperCase() === 'LABC');
  }

  /** True if any request is microbiología (Func. 4 botón Epidemiología / Sensibilidad ATB). */
  get patientHasMicrobiology(): boolean {
    return this.records.some((r) => r.labType === LAB_TYPE.LABM);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: PatientRequestsService,
    private locationService: LocationService,
    private userService: UsersService,
    private msgbox: MsgboxService,
    private dialogService: DialogService,
    private translate: TranslateService
  ) {
    // get state from router
    this.dataFromState = this.router.getCurrentNavigation()?.extras.state?.['data'];
  }

  public ngOnInit(): void {
    if (this.route.snapshot.params['id']) {
      this.getRecordsFromPatient();
      this.isPatientSearch = true;
    } else if (this.route.snapshot.queryParams) {
      this.getRecordsFromSearch();
    }

    this.patient$.subscribe((patient) => {
      if (patient && !this._patientReceivedButNotComingFromHistory) {
        this.breadcrumbs = BREADCRUMBS_WITH_PATIENT;
      } else {
        this.breadcrumbs = BREADCRUMBS_WITHOUT_PATIENT;
      }
    });

    this.records$.subscribe((records) => {
      this.records = records.map((record) => {
        // WIR-RF-01.02.6.5: unificar nombres para ordenar por cualquier columna (materialName/statName llegan a veces con distinta capitalización)
        const materialName = record.materialName ?? record.materialname;
        const statName = record.statName ?? record.statname;
        const raw = record as IPatientRequest & { nDocs?: number; Alertas?: string };
        return {
          ...record,
          einDat:
            record.einDat instanceof Date
              ? record.einDat
              : new Date(record.einDat),
          materialName: materialName ?? '',
          statName: statName ?? '',
          ndocs: Number(raw.ndocs ?? raw.nDocs ?? 0),
          alertas: String(raw.alertas ?? raw.Alertas ?? '').trim(),
        };
      });
      if (this.records.length > 0) {
        const fromMeta =
          this.records[0].columnas
            ?.split(';')
            .map((col) => col.trim().toLowerCase())
            .filter((c) => c.length > 0) || [];
        const cols = [...fromMeta];
        const hasNdocsValue = this.records.some((r) => {
          const row = r as IPatientRequest & { nDocs?: number };
          return row.ndocs != null || row.nDocs != null;
        });
        if (hasNdocsValue && !cols.includes('ndocs')) {
          cols.push('ndocs');
        }
        this.columns = cols;
        const ordenParts =
          this.records.length > 0
            ? this.records[0].orden?.split('+') ?? ['patVorname', 'ASC']
            : ['patVorname', 'ASC'];
        const [_sortField, _sortOrder] = ordenParts;

        this.sortField = this.convertTitleCaseToCamelCase(_sortField);
        this.sortOrder = _sortOrder === 'ASC' ? 1 : -1;
      }
    });
  }

  private convertTitleCaseToCamelCase(title: string): string {
    // convert first char to lowercase
    return title.charAt(0).toLowerCase() + title.slice(1);
  }

  private getRecordsFromSearch() {
    if (this.dataFromState) {
      // If we have data from state, use it
      this.records$ = new Observable<IPatientRequest[]>((observer) => {
        observer.next(this.dataFromState);
        observer.complete();
      });
    } else {
      const queryParams = this.route.snapshot.queryParams;
      const params = queryParams['prms'] == null
        ? { ...queryParams }
        : this.locationService.decodeParams(queryParams);
      this.records$ = this.service.searchRequests(params).pipe(
        catchError((err) => {
          const key = err?.message || err?.error?.message;
          if (key && typeof key === 'string' && key.startsWith('WEB_RESULTS.ERROR.')) {
            this.msgbox.showError(key);
          } else {
            this.msgbox.showError('ERRORS.INTERNAL_SERVER_ERROR');
          }
          return of([]);
        })
      );
    }
  }

  private getRecordsFromPatient() {
    // Sync default center from URL when present (e.g. after refresh)
    const q = this.route.snapshot.queryParams['centroId'];
    if (q != null && q !== '') {
      const c = Number(q);
      if (!Number.isNaN(c)) {
        this.userService.setSisifoDefaultCenter(c);
      }
    }

    this.records$ = combineLatest([
      this.route.params,
      this.route.queryParams,
    ]).pipe(
      switchMap(([params, queryParams]) => {
        const id = params['id'];
        const centroId =
          (queryParams['centroId'] != null && queryParams['centroId'] !== ''
            ? Number(queryParams['centroId'])
            : null) ?? this.userService.getSisifoDefaultCenter();
        const searchType = (queryParams['searchType'] as SearchType) || SearchType.Center;
        return this.service.getPatientRequests({
          historia: id,
          searchType,
          centroId,
        } as any);
      }),
      catchError((err) => {
        const key = err?.message || err?.error?.message;
        if (key && typeof key === 'string' && key.startsWith('WEB_RESULTS.ERROR.')) {
          this.msgbox.showError(key);
        } else {
          this.msgbox.showError('ERRORS.INTERNAL_SERVER_ERROR');
        }
        return of([]);
      })
    );

    combineLatest([this.route.params, this.route.queryParams])
      .pipe(
        switchMap(([params, queryParams]) => {
          const id = params['id'];
          const centroId =
            (queryParams['centroId'] != null && queryParams['centroId'] !== ''
              ? Number(queryParams['centroId'])
              : null) ?? this.userService.getSisifoDefaultCenter();
          return this.service
            .getPatientHistories({
              history: id,
              searchType: (queryParams['searchType'] as SearchType) || SearchType.Center,
              centroId,
            } as any)
            .pipe(
              catchError((err) => {
                const key = err?.error?.message || err?.message;
                if (key && typeof key === 'string' && key.startsWith('WEB_RESULTS.ERROR.')) {
                  this.msgbox.showError(key);
                } else {
                  this.msgbox.showError('ERRORS.INTERNAL_SERVER_ERROR');
                }
                return of([]);
              }),
              map((patients) => (Array.isArray(patients) ? patients.find((p) => p.historia === id) : null))
            );
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe((patient) => {
        this.patient$.next(patient);
      });
  }

  private loadPatientFromHistory(history: any): void {
    const centroId =
      (this.route.snapshot.queryParams['centroId'] != null &&
      this.route.snapshot.queryParams['centroId'] !== ''
        ? Number(this.route.snapshot.queryParams['centroId'])
        : null) ?? this.userService.getSisifoDefaultCenter();
    const searchType = (this.route.snapshot.queryParams['searchType'] as SearchType) || SearchType.Center;
    this.service
      .getPatientHistories({
        history,
        searchType,
        centroId,
      } as any)
      .pipe(
        catchError((err) => {
          const key = err?.error?.message || err?.message;
          if (key && typeof key === 'string' && key.startsWith('WEB_RESULTS.ERROR.')) {
            this.msgbox.showError(key);
          } else {
            this.msgbox.showError('ERRORS.INTERNAL_SERVER_ERROR');
          }
          return of([]);
        }),
        map((patients) => (Array.isArray(patients) ? patients.find((p) => p.historia === history) : null)),
        takeUntil(this.destroyed$)
      )
      .subscribe((patient) => {
        this.patient$.next(patient);
      });
  }

  public onMaterialSelect(material: IPatientRequestMaterial): void {
    this.selectedRecord$.next({
      ...this.currentHeader,
      ...material,
      materiales: [material],
      tests: undefined,
    });
    this.selectedMaterial = material;
  }

  public onRowSelect(event: any): void {
    this.service
      .getPatientRequestDetail(
        event.laboratorio,
        event.peticion,
        event.labor,
        event.cEinDat,
        event.impreso,
        event.sitio
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe((record) => {
        if (!this.patient$.getValue()) {
          this._patientReceivedButNotComingFromHistory = true;
        }
        this.loadPatientFromHistory(record.aZ);

        if (
          record.labType === LAB_TYPE.LABM ||
          (!record.labType && record.labName?.toUpperCase().startsWith('MIC'))
        ) {
          this.selectedRecord$.next(null);

          this.materiales = record.materiales;

          const defaultMaterial = this.materiales.find(
            (m) => m.materialNr === event?.materialNr
          );

          this.currentHeader = record;
          if (defaultMaterial) {
            this.onMaterialSelect(defaultMaterial);
          }
        } else {
          this.materiales = [];
          this.selectedRecord$.next(record);
        }
        this.scrollToSelectedRequestResults();
      });
  }

  private scrollToSelectedRequestResults(): void {
    globalThis.setTimeout(() => {
      this.selectedRequestResults?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }

  /** Open dialog 2.2 Búsqueda de acumulados (solo desde pantalla paciente con al menos una petición LABC). */
  public openAcumuladosDialog(): void {
    const patientId = this.patient$.getValue()?.historia ?? this.route.snapshot.params['id'] ?? '';
    const labNames = new Map<string, number>();
    this.records
      .filter((r) => (r.labType || '').trim().toUpperCase() === 'LABC' && (r.laboratorio || '').trim())
      .forEach((r) => {
        const name = (r.laboratorio || '').trim();
        if (!labNames.has(name)) labNames.set(name, r.labor);
      });
    const labOptions = Array.from(labNames.entries()).map(([label, labor]) => ({
      label,
      value: label,
      labNr: typeof labor === 'number' ? labor : undefined,
    }));
    this.dialogService.open(AcumuladosSearchDialogComponent, {
      header: 'Búsqueda de acumulados',
      width: 'min(1000px, 96vw)',
      data: {
        patientId,
        patient: this.patient$.getValue(),
        labOptions,
      } as import('../acumulados-search-dialog/acumulados-search-dialog.component').AcumuladosSearchDialogData,
    });
  }

  /** Diálogo Func. 4 — mismos criterios que panel 3.1; `tLabNr` del primer LABM (selector si hay varios). */
  public openEpidemiologyDialog(): void {
    const patientId = this.patient$.getValue()?.historia ?? this.route.snapshot.params['id'] ?? '';
    const labByName = new Map<
      string,
      { labNr: number; detailLaboratorio: string }
    >();
    this.records
      .filter((r) => r.labType === LAB_TYPE.LABM && String(r.laboratorio ?? '').trim())
      .forEach((r) => {
        const name = String(r.laboratorio).trim();
        if (!labByName.has(name)) {
          const prefix = name.slice(0, 3).toUpperCase() || 'MIC';
          labByName.set(name, {
            labNr: Number(r.labor),
            detailLaboratorio: prefix,
          });
        }
      });
    const labOptions = Array.from(labByName.entries()).map(([label, v]) => ({
      label,
      value: label,
      labNr: v.labNr,
      detailLaboratorio: v.detailLaboratorio,
    }));
    if (!labOptions.length) {
      return;
    }
    const data: EpidemiologySearchDialogData = {
      patientId,
      patient: this.patient$.getValue(),
      labOptions,
      centroId: this.userService.getSisifoDefaultCenter(),
    };
    this.dialogService.open(EpidemiologySearchDialogComponent, {
      header: this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.DIALOG_TITLE'),
      width: 'min(640px, 96vw)',
      data,
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
